import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { apiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Loader2, Video, MapPin, Trash2, ChevronRight, Sparkles, Calendar, Columns2,
  Plus, PenLine, Star, Wind, Clock, Gauge,
} from 'lucide-react';
import { toast } from 'sonner';

const WIND_DIRS = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
const todayISO = () => new Date().toISOString().slice(0, 10);
const EMPTY = { date: todayISO(), spot_id: '', duration_min: '', wind_knots: '', wind_direction: '', kite_size: '', board: '', rating: 0, notes: '' };

export default function SessionLog() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [analyses, setAnalyses] = useState([]);
  const [manual, setManual] = useState([]);
  const [spots, setSpots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const [a, m, s] = await Promise.allSettled([
      api.get('/analyses'), api.get('/sessions/manual'), api.get('/spots'),
    ]);
    if (a.status === 'fulfilled') setAnalyses(a.value.data);
    if (m.status === 'fulfilled') setManual(m.value.data);
    if (s.status === 'fulfilled') setSpots(s.value.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const overall = (s) => { const sc = s.result?.scores; return sc ? (Object.values(sc).reduce((p, c) => p + c, 0) / Object.values(sc).length).toFixed(1) : null; };

  // Unified, date-sorted feed of AI analyses + manual sessions.
  const feed = useMemo(() => {
    const aItems = analyses.map((s) => ({ kind: 'analysis', sortDate: s.created_at || '', data: s }));
    const mItems = manual.map((s) => ({ kind: 'manual', sortDate: `${s.date}T23:59:59`, data: s }));
    return [...aItems, ...mItems].sort((x, y) => (x.sortDate < y.sortDate ? 1 : -1));
  }, [analyses, manual]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        date: form.date || todayISO(),
        spot_id: form.spot_id || null,
        duration_min: form.duration_min ? Number(form.duration_min) : null,
        wind_knots: form.wind_knots ? Number(form.wind_knots) : null,
        wind_direction: form.wind_direction || null,
        kite_size: form.kite_size ? Number(form.kite_size) : null,
        board: form.board || null,
        rating: form.rating || null,
        notes: form.notes || null,
      };
      const { data } = await api.post('/sessions/manual', payload);
      setManual((m) => [data, ...m]);
      toast.success(t('sessions.logged'));
      setOpen(false); setForm(EMPTY);
    } catch (err) {
      toast.error(apiError(err, t('sessions.logError')));
    } finally { setSaving(false); }
  };

  const delAnalysis = async (id, e) => {
    e.preventDefault(); e.stopPropagation();
    try { await api.delete(`/analyses/${id}`); setAnalyses((x) => x.filter((i) => i.id !== id)); toast.success(t('sessions.deleted')); }
    catch { toast.error(t('sessions.deleteError')); }
  };
  const delManual = async (id, e) => {
    e.preventDefault(); e.stopPropagation();
    try { await api.delete(`/sessions/manual/${id}`); setManual((x) => x.filter((i) => i.id !== id)); toast.success(t('sessions.deleted')); }
    catch { toast.error(t('sessions.deleteError')); }
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#00B4D8] animate-spin" /></div>;

  const completedCount = analyses.filter((s) => s.status === 'completed').length;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
        <div>
          <div className="label-eyebrow text-[#48CAE4] mb-2">{t('sessions.eyebrow')}</div>
          <h1 className="font-display text-4xl sm:text-5xl">{t('sessions.title')}</h1>
          <p className="text-muted-foreground mt-1">{t('sessions.count', { count: feed.length })}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={() => setOpen(true)} variant="outline" className="border-[#2EE59D]/30 bg-[#2EE59D]/10 text-[#2EE59D] hover:bg-[#2EE59D]/20 rounded-xl" data-testid="log-session-btn">
            <Plus className="w-4 h-4 mr-2" /> {t('sessions.logSession')}
          </Button>
          {completedCount >= 2 && (
            <Button onClick={() => navigate('/compare')} variant="outline" className="border-white/20 bg-white/5 hover:bg-white/10 rounded-xl" data-testid="sessions-compare-btn"><Columns2 className="w-4 h-4 mr-2" /> {t('sessions.compare')}</Button>
          )}
          <Button onClick={() => navigate('/analyze')} className="bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl"><Sparkles className="w-4 h-4 mr-2" /> {t('sessions.newAnalysis')}</Button>
        </div>
      </div>

      {feed.length === 0 ? (
        <div className="kite-card p-12 text-center" data-testid="sessions-empty">
          <Video className="w-10 h-10 text-muted-foreground mx-auto" />
          <p className="mt-4 text-muted-foreground">{t('sessions.emptyText')}</p>
          <div className="flex items-center justify-center gap-2 mt-5">
            <Button onClick={() => navigate('/analyze')} className="bg-[#00B4D8] text-black font-semibold rounded-xl">{t('sessions.analyzeASession')}</Button>
            <Button onClick={() => setOpen(true)} variant="outline" className="border-white/20 bg-white/5 hover:bg-white/10 rounded-xl">{t('sessions.logSession')}</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="sessions-feed">
          {feed.map((row) => row.kind === 'analysis' ? (
            <Link key={`a-${row.data.id}`} to={`/analysis/${row.data.id}`} className="kite-card p-3 flex items-center gap-4 group" data-testid={`session-row-${row.data.id}`}>
              <div className="w-24 h-16 rounded-lg overflow-hidden bg-[#0A0A0A] shrink-0">
                {row.data.thumbnail ? <img src={row.data.thumbnail} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Video className="w-5 h-5 text-muted-foreground" /></div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium capitalize truncate">{row.data.result?.skill_level_detected || row.data.status} {t('sessions.sessionWord')}</span>
                  <Badge variant="outline" className="border-[#00B4D8]/30 bg-[#00B4D8]/10 text-[#00B4D8] text-[10px] shrink-0"><Sparkles className="w-3 h-3 mr-1" /> {t('sessions.typeAi')}</Badge>
                </div>
                <div className="text-xs text-muted-foreground flex items-center gap-3 mt-1 flex-wrap">
                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {(row.data.created_at || '').slice(0, 10)}</span>
                  {row.data.meta?.spot_name && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {row.data.meta.spot_name}</span>}
                  {row.data.meta?.wind_knots && <span>{row.data.meta.wind_knots}kn {row.data.meta.wind_direction || ''}</span>}
                </div>
              </div>
              {row.data.status === 'processing' && <span className="text-xs text-[#48CAE4] flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> {t('sessions.analyzing')}</span>}
              {overall(row.data) && <span className="font-data text-lg font-semibold text-[#00B4D8]">{overall(row.data)}<span className="text-xs text-muted-foreground">/10</span></span>}
              <button onClick={(e) => delAnalysis(row.data.id, e)} className="text-muted-foreground hover:text-[#FF3B3B] p-2" data-testid={`session-delete-${row.data.id}`}><Trash2 className="w-4 h-4" /></button>
              <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-[#48CAE4]" />
            </Link>
          ) : (
            <div key={`m-${row.data.id}`} className="kite-card p-3 flex items-center gap-4" data-testid={`manual-row-${row.data.id}`}>
              <div className="w-24 h-16 rounded-lg bg-[#0A0A0A] shrink-0 flex items-center justify-center border border-white/8">
                <PenLine className="w-5 h-5 text-[#2EE59D]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium truncate">{row.data.spot_name || t('sessions.manualSession')}</span>
                  <Badge variant="outline" className="border-[#2EE59D]/30 bg-[#2EE59D]/10 text-[#2EE59D] text-[10px] shrink-0"><PenLine className="w-3 h-3 mr-1" /> {t('sessions.typeManual')}</Badge>
                </div>
                <div className="text-xs text-muted-foreground flex items-center gap-3 mt-1 flex-wrap">
                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {row.data.date}</span>
                  {row.data.duration_min != null && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {row.data.duration_min}m</span>}
                  {row.data.wind_knots != null && <span className="flex items-center gap-1"><Wind className="w-3 h-3" /> {row.data.wind_knots}kn {row.data.wind_direction || ''}</span>}
                  {row.data.kite_size != null && <span className="flex items-center gap-1"><Gauge className="w-3 h-3" /> {row.data.kite_size}m²</span>}
                </div>
                {row.data.notes && <p className="text-xs text-white/70 mt-1.5 line-clamp-2">{row.data.notes}</p>}
              </div>
              {row.data.rating ? (
                <span className="flex items-center gap-0.5 shrink-0" data-testid="manual-rating">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="w-3.5 h-3.5" fill={i < row.data.rating ? '#FFB020' : 'none'} stroke={i < row.data.rating ? '#FFB020' : '#3a3f45'} />
                  ))}
                </span>
              ) : null}
              <button onClick={(e) => delManual(row.data.id, e)} className="text-muted-foreground hover:text-[#FF3B3B] p-2" data-testid={`manual-delete-${row.data.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}

      {/* Manual session dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#12161B] border-white/12 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="manual-session-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2"><PenLine className="w-5 h-5 text-[#2EE59D]" /> {t('sessions.logTitle')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ms-date">{t('sessions.fDate')}</Label>
                <Input id="ms-date" type="date" value={form.date} max={todayISO()} onChange={(e) => setF('date', e.target.value)} required className="mt-1.5 bg-[#0A0A0A] border-white/15" data-testid="ms-date" />
              </div>
              <div>
                <Label>{t('sessions.fSpot')}</Label>
                <Select value={form.spot_id || 'none'} onValueChange={(v) => setF('spot_id', v === 'none' ? '' : v)}>
                  <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-white/15" data-testid="ms-spot"><SelectValue placeholder={t('sessions.fSpotNone')} /></SelectTrigger>
                  <SelectContent className="bg-[#12161B] border-white/15 text-white max-h-60">
                    <SelectItem value="none">{t('sessions.fSpotNone')}</SelectItem>
                    {spots.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {s.country}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label htmlFor="ms-dur">{t('sessions.fDuration')}</Label>
                <Input id="ms-dur" type="number" min="0" value={form.duration_min} onChange={(e) => setF('duration_min', e.target.value)} placeholder="90" className="mt-1.5 bg-[#0A0A0A] border-white/15" data-testid="ms-duration" />
              </div>
              <div>
                <Label htmlFor="ms-wind">{t('sessions.fWind')}</Label>
                <Input id="ms-wind" type="number" min="0" step="0.5" value={form.wind_knots} onChange={(e) => setF('wind_knots', e.target.value)} placeholder="20" className="mt-1.5 bg-[#0A0A0A] border-white/15" data-testid="ms-wind" />
              </div>
              <div>
                <Label>{t('sessions.fDir')}</Label>
                <Select value={form.wind_direction || 'none'} onValueChange={(v) => setF('wind_direction', v === 'none' ? '' : v)}>
                  <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-white/15" data-testid="ms-dir"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-[#12161B] border-white/15 text-white max-h-60">
                    <SelectItem value="none">—</SelectItem>
                    {WIND_DIRS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ms-kite">{t('sessions.fKite')}</Label>
                <Input id="ms-kite" type="number" min="0" step="0.5" value={form.kite_size} onChange={(e) => setF('kite_size', e.target.value)} placeholder="9" className="mt-1.5 bg-[#0A0A0A] border-white/15" data-testid="ms-kite" />
              </div>
              <div>
                <Label htmlFor="ms-board">{t('sessions.fBoard')}</Label>
                <Input id="ms-board" value={form.board} onChange={(e) => setF('board', e.target.value)} placeholder="Twintip 138" className="mt-1.5 bg-[#0A0A0A] border-white/15" data-testid="ms-board" />
              </div>
            </div>

            <div>
              <Label>{t('sessions.fRating')}</Label>
              <div className="flex items-center gap-1.5 mt-1.5" data-testid="ms-rating">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => setF('rating', form.rating === n ? 0 : n)} className="p-1 transition-transform hover:scale-110" data-testid={`ms-star-${n}`}>
                    <Star className="w-6 h-6" fill={n <= form.rating ? '#FFB020' : 'none'} stroke={n <= form.rating ? '#FFB020' : '#6b7280'} />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="ms-notes">{t('sessions.fNotes')}</Label>
              <Textarea id="ms-notes" value={form.notes} onChange={(e) => setF('notes', e.target.value)} placeholder={t('sessions.fNotesPh')} rows={3} className="mt-1.5 bg-[#0A0A0A] border-white/15 resize-none" data-testid="ms-notes" />
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="border-white/15 bg-transparent hover:bg-white/10 rounded-xl">{t('sessions.cancel')}</Button>
              <Button type="submit" disabled={saving} className="bg-[#2EE59D] hover:bg-[#26c889] text-black font-semibold rounded-xl" data-testid="ms-submit">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t('sessions.saveSession')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
